import { Link } from "react-router-dom";
import { ArrowRight, CalendarX2, Newspaper, Shield, Trophy, Radio } from "lucide-react";
import { useFetch } from "@/lib/api";
import { usePageMeta, SectionTitle, EmptyState, Loader, ErrorState, StatusBadge } from "@/components/common";
import { EventCard } from "@/components/EventCard";
import { StandingsTable } from "@/components/StandingsTable";
import { Podium } from "@/components/Podium";
import { fmtDate, fmtDateTime, NEWS_CAT } from "@/lib/format";

function Hero({ tagline }) {
  return (
    <section data-testid="home-hero" className="relative min-h-[88vh] flex items-center overflow-hidden grain">
      <img src="/images/hero.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      <div className="absolute -bottom-40 -left-20 w-[900px] h-[500px] purple-glow" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-24 pb-20 w-full">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="max-w-3xl fade-up order-2 lg:order-1">
          <div className="eyebrow mb-5">Alleanza competitiva · Call of Duty</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[0.95]">
            <span className="silver-text">La piattaforma</span><br /><span className="text-[#F5F5F7]">ufficiale</span><span className="sr-only"> di NEXUM LEGACY</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#C6C6CE]/80 max-w-xl">{tagline || "Eventi, classifiche live e risultati ufficiali dell'alleanza, in un unico punto di riferimento."}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link data-testid="hero-events-button" to="/eventi" className="btn-primary">Scopri gli eventi <ArrowRight className="w-4 h-4" /></Link>
            <Link data-testid="hero-standings-button" to="/classifiche" className="btn-ghost">Consulta le classifiche</Link>
          </div>
        </div>
        <div className="order-1 lg:order-2 flex justify-center lg:justify-end fade-up">
          <img data-testid="hero-logo" src="/images/logo.webp" alt="NEXUM LEGACY" width="640" height="640" className="w-60 sm:w-80 lg:w-[460px] h-auto [mask-image:radial-gradient(circle,black_55%,transparent_72%)]" />
        </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedEvent({ ev }) {
  return (
    <Link to={`/eventi/${ev.slug}`} data-testid="featured-event" className="group relative block nx-card overflow-hidden nx-card-hover">
      <div className="grid md:grid-cols-5">
        <div className="md:col-span-3 aspect-[16/9] md:aspect-auto md:min-h-[340px] relative bg-[#17171D]">
          <img src={ev.poster_url || "/images/texture.jpg"} alt={`Locandina ${ev.title}`} className="absolute inset-0 w-full h-full object-cover opacity-80" />
        </div>
        <div className="md:col-span-2 p-7 md:p-10 flex flex-col justify-center">
          <div className="eyebrow mb-3">Evento principale</div>
          <StatusBadge status={ev.status} />
          <h3 className="mt-4 text-2xl md:text-3xl font-bold uppercase text-[#F5F5F7] group-hover:text-[#d6b3ff] transition-colors duration-200">{ev.title}</h3>
          <p className="mt-3 text-sm text-[#C6C6CE]/70 line-clamp-3">{ev.description}</p>
          <div className="mt-6 text-xs text-[#C6C6CE]/60 font-num">{fmtDateTime(ev.start_at)}</div>
        </div>
      </div>
    </Link>
  );
}

const Section = ({ children, testId }) => <section data-testid={testId} className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">{children}</section>;
const More = ({ to, label, testId }) => <Link data-testid={testId} to={to} className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C6C6CE]/70 hover:text-[#F5F5F7] transition-colors duration-200">{label} <ArrowRight className="w-3.5 h-3.5" /></Link>;

export default function Home() {
  usePageMeta(null, "NEXUM LEGACY: eventi, classifiche live e risultati ufficiali dell'alleanza competitiva Call of Duty.");
  const { data, loading, error, reload } = useFetch("/home");
  if (loading && !data) return <><Hero /><Loader /></>;
  if (error) return <><Hero /><div className="max-w-3xl mx-auto px-5 py-16"><ErrorState message={error} onRetry={reload} /></div></>;
  const d = data;
  return (
    <div data-testid="home-page">
      <Hero tagline={d.settings?.tagline} />
      {d.featured_news && (
        <div className="max-w-7xl mx-auto px-5 md:px-8 -mt-8 relative">
          <Link to={`/news/${d.featured_news.slug}`} data-testid="featured-news-banner" className="flex items-center gap-4 nx-card px-5 py-4 border-[#962FFF]/30 hover:border-[#962FFF]/60 transition-colors duration-200">
            <span className="eyebrow shrink-0">In evidenza</span>
            <span className="text-sm text-[#F5F5F7] truncate">{d.featured_news.title}</span>
            <ArrowRight className="w-4 h-4 ml-auto text-[#962FFF] shrink-0" />
          </Link>
        </div>
      )}
      <Section testId="home-featured-section">
        <SectionTitle eyebrow="In primo piano" title="Evento principale" action={<More to="/eventi" label="Tutti gli eventi" testId="home-all-events-link" />} />
        {d.featured_event ? <FeaturedEvent ev={d.featured_event} /> : <EmptyState icon={CalendarX2} title="Nessun evento in evidenza" text="L'evento principale verrà mostrato qui non appena lo staff lo pubblicherà." />}
      </Section>
      <Section testId="home-live-section">
        <SectionTitle eyebrow="Adesso" title="Eventi live" action={<More to="/classifiche" label="Classifiche" testId="home-standings-link" />} />
        {d.live.length ? (
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 grid gap-4">{d.live.slice(0, 2).map((e, i) => <EventCard key={e.id} ev={e} index={i} />)}</div>
            <div className="lg:col-span-3">
              {d.live_standings && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm text-[#F5F5F7] font-display uppercase tracking-wider"><Radio className="w-4 h-4 text-[#962FFF] live-dot" /> {d.live_standings.event.title}</div>
                    <Link to={`/classifiche/${d.live_standings.event.slug}`} data-testid="home-live-full-standings" className="text-xs text-[#962FFF] hover:underline">Classifica completa</Link>
                  </div>
                  {d.live_standings.rows.length ? <StandingsTable rows={d.live_standings.rows} compact /> : <EmptyState title="Classifica in attesa dei primi risultati" />}
                </div>
              )}
            </div>
          </div>
        ) : <EmptyState icon={Radio} title="Nessun evento in corso" text="Quando un evento sarà live, qui troverai la classifica aggiornata in tempo reale." />}
      </Section>
      <Section testId="home-upcoming-section">
        <SectionTitle eyebrow="Calendario" title="Prossimi eventi" />
        {d.upcoming.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{d.upcoming.map((e, i) => <EventCard key={e.id} ev={e} index={i} />)}</div>
          : <EmptyState icon={CalendarX2} title="Nessun evento in programma" text="I prossimi eventi verranno annunciati dallo staff." />}
      </Section>
      <Section testId="home-podium-section">
        <SectionTitle eyebrow="Ultimo evento concluso" title={d.podium ? d.podium.event?.title : "Podio"} action={d.podium && <More to={`/risultati/${d.podium.event?.slug}`} label="Risultati ufficiali" testId="home-podium-link" />} />
        {d.podium ? <div className="nx-card p-6 md:p-10 relative overflow-hidden"><div className="absolute inset-x-0 -top-32 h-64 purple-glow" /><div className="relative"><Podium rows={d.podium.final.rows} /></div></div>
          : <EmptyState icon={Trophy} title="Nessun risultato ufficiale" text="Il podio comparirà dopo la pubblicazione della prima classifica ufficiale." />}
      </Section>
      <Section testId="home-clans-section">
        <SectionTitle eyebrow="Alleanza" title="Clan partecipanti" action={<More to="/clan" label="Tutti i clan" testId="home-all-clans-link" />} />
        {d.clans.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {d.clans.map((c) => (
              <Link key={c.id} to={`/clan/${c.slug}`} data-testid={`home-clan-${c.slug}`} className="nx-card nx-card-hover p-5 flex items-center gap-3">
                <ClanLogo clan={c} />
                <div className="min-w-0"><div className="font-display font-semibold uppercase text-[#F5F5F7] truncate">{c.name}</div><div className="font-num text-[11px] text-[#C6C6CE]/60">[{c.tag}]</div></div>
              </Link>
            ))}
          </div>
        ) : <EmptyState icon={Shield} title="Nessun clan registrato" text="I clan dell'alleanza saranno elencati qui." />}
      </Section>
      <Section testId="home-news-section">
        <SectionTitle eyebrow="Comunicazioni" title="News recenti" action={<More to="/news" label="Tutte le news" testId="home-all-news-link" />} />
        {d.news.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{d.news.map((n) => <NewsCard key={n.id} n={n} />)}</div>
          : <EmptyState icon={Newspaper} title="Nessuna comunicazione pubblicata" />}
      </Section>
      <Section testId="home-alliance-section">
        <div className="nx-card p-8 md:p-14 grid md:grid-cols-3 gap-8 relative overflow-hidden">
          <div className="absolute -right-40 -top-40 w-[500px] h-[500px] purple-glow" />
          <div className="relative"><div className="eyebrow mb-3">Chi siamo</div><h2 className="text-2xl md:text-3xl font-bold uppercase text-[#F5F5F7]">L'alleanza</h2></div>
          <div className="relative md:col-span-2">
            <p className="prose-nx">{d.presentazione?.content || "La presentazione istituzionale dell'alleanza sarà pubblicata a breve dallo staff."}</p>
            <Link to="/alleanza" data-testid="home-alliance-link" className="btn-ghost mt-8">Scopri NEXUM LEGACY</Link>
          </div>
        </div>
      </Section>
    </div>
  );
}

export function ClanLogo({ clan, size = "w-11 h-11" }) {
  return clan.logo_url ? (
    <img src={clan.logo_url} alt={`Logo ${clan.name}`} loading="lazy" className={`${size} rounded-md object-cover border border-[#C6C6CE]/15 shrink-0`} />
  ) : (
    <div className={`${size} rounded-md border border-[#C6C6CE]/15 bg-[#17171D] flex items-center justify-center font-num text-xs text-[#962FFF] shrink-0`}>{clan.tag?.slice(0, 3)}</div>
  );
}

export function NewsCard({ n }) {
  return (
    <Link to={`/news/${n.slug}`} data-testid={`news-card-${n.slug}`} className="group nx-card nx-card-hover overflow-hidden flex flex-col">
      <div className="aspect-[16/10] bg-[#17171D] overflow-hidden">
        <img src={n.image_url || "/images/texture.jpg"} alt="" loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest"><span className="text-[#962FFF]">{NEWS_CAT[n.category]}</span><span className="text-[#C6C6CE]/40">{fmtDate(n.publish_at)}</span></div>
        <h3 className="mt-2 font-display font-semibold text-[#F5F5F7] leading-snug group-hover:text-[#d6b3ff] transition-colors duration-200 line-clamp-2">{n.title}</h3>
        {n.excerpt && <p className="mt-2 text-xs text-[#C6C6CE]/60 line-clamp-2">{n.excerpt}</p>}
      </div>
    </Link>
  );
}
